import React, { useState, useContext, useRef } from 'react';
import { Printer, Mail, Plus, Minus, Check, Download } from 'lucide-react';
import * as htmlToImage from 'html-to-image';
import { AuthContext } from '../App';

const ICO = {
    pin: `<svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z"/><circle cx="12" cy="10" r="3"/></svg>`,
    phone: `<svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/></svg>`,
    mail: `<svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><rect width="20" height="16" x="2" y="4" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/></svg>`,
    globe: `<svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><path d="M12 2a14.5 14.5 0 0 0 0 20 14.5 14.5 0 0 0 0-20"/><path d="M2 12h20"/></svg>`,
};

const getEnvelopeTemplates = (companyName, logoUrl, companyData) => {
    const slug = companyName.toLowerCase().replace(/\s+/g, '');

    // Default data
    const address = companyData?.address || 'Shop No. 04, Near Landmark,<br/>Virar (W), Palghar - 401303';
    const contact = companyData?.contact_no || '+91 98XXX XXXXX';
    const email = companyData?.email || `info@${slug}.com`;
    const website = companyData?.website || `www.${slug}.com`;
    const gstin = companyData?.gstin_no || '27AAAAA0000A1Z5';

    // Footer block optimized for DL vertical stacking on the left
    const sidebarContact = (iconColor, textColor) => `
        <div style="display:flex;flex-direction:column;gap:6px;margin-top:16px;">
            <div style="display:flex;align-items:flex-start;gap:6px;">
                <div style="color:${iconColor};margin-top:1px;">${ICO.pin}</div>
                <div style="font-size:7px;font-weight:600;color:${textColor};line-height:1.3;">${address}</div>
            </div>
            <div style="display:flex;align-items:center;gap:6px;">
                <div style="color:${iconColor};">${ICO.phone}</div>
                <div style="font-size:7.5px;font-weight:600;color:${textColor};">${contact}</div>
            </div>
            <div style="display:flex;align-items:center;gap:6px;">
                <div style="color:${iconColor};">${ICO.mail}</div>
                <div style="font-size:7.5px;font-weight:600;color:${textColor};">${email}</div>
            </div>
        </div>
    `;

    return [
        {
            id: 'corporate',
            name: 'Modern Industrial',
            desc: 'Clean DL format with indigo side panel',
            accent: '#4f46e5',
            preview: { headerBg: '#0f172a', accent: '#4f46e5' },
            render: () => `
                <div class="env-page" style="width:100%;height:100%;box-sizing:border-box;display:flex;background:#ffffff;position:relative;">
                    <div style="width:75mm;background:linear-gradient(180deg,#0f172a,#1e293b);padding:20px;display:flex;flex-direction:column;">
                        <img src="${logoUrl}" alt="Logo" style="height:48px;width:auto;max-width:100%;object-fit:contain;filter:brightness(0) invert(1);margin-bottom:12px;"/>
                        <h1 style="font-size:16px;font-weight:900;color:#ffffff;margin:0;letter-spacing:0.5px;text-transform:uppercase;line-height:1.1;">${companyName}</h1>
                        <p style="font-size:6px;font-weight:700;color:#94a3b8;text-transform:uppercase;letter-spacing:2px;margin:4px 0 0 0;">INTERIOR DESIGNERS & FURNITURE MANUFACTURERS</p>
                        <div style="margin-top:auto;">
                            <div style="height:1px;background:#334155;margin-bottom:12px;"></div>
                            ${sidebarContact('#6366f1', '#cbd5e1')}
                        </div>
                    </div>
                    <div style="flex:1;position:relative;padding:30px;">
                        <div style="position:absolute;bottom:20px;right:20px;text-align:right;">
                            <p style="font-size:7px;font-weight:800;color:#94a3b8;text-transform:uppercase;letter-spacing:2px;margin:0 0 2px;">GSTIN</p>
                            <p style="font-size:11px;font-weight:900;color:#0f172a;letter-spacing:1px;margin:0;font-family:'Courier New',monospace;">${gstin}</p>
                        </div>
                        <div style="position:absolute;top:50%;left:50%;transform:translate(-50%,-50%);opacity:0.02;pointer-events:none;white-space:nowrap;">
                            <div style="font-size:60px;font-weight:900;color:#0f172a;text-transform:uppercase;letter-spacing:8px;line-height:1;text-align:center;">${companyName}</div>
                        </div>
                    </div>
                </div>
            `
        },
        {
            id: 'royal',
            name: 'Classic Timber',
            desc: 'Luxurious oak borders for standard DL',
            accent: '#b45309',
            preview: { headerBg: '#78350f', accent: '#f59e0b' },
            render: () => `
                <div class="env-page" style="width:100%;height:100%;box-sizing:border-box;background:#fffbeb;position:relative;border:4px solid #78350f;padding:24px;">
                    <div style="display:flex;align-items:flex-start;justify-content:space-between;border-bottom:2px double #b45309;padding-bottom:16px;">
                        <div style="display:flex;align-items:center;gap:12px;">
                            <img src="${logoUrl}" alt="Logo" style="height:48px;width:auto;max-width:160px;object-fit:contain;"/>
                            <div>
                                <h1 style="font-size:18px;font-weight:900;color:#78350f;margin:0;text-transform:uppercase;letter-spacing:2px;line-height:1;">${companyName}</h1>
                                <p style="font-size:6px;font-weight:700;color:#92400e;text-transform:uppercase;letter-spacing:3px;margin:4px 0 0 0;">INTERIOR DESIGNERS & FURNITURE MANUFACTURERS</p>
                            </div>
                        </div>
                        <div style="text-align:right;">
                            <p style="font-size:7px;font-weight:800;color:#92400e;text-transform:uppercase;letter-spacing:2px;margin:0 0 2px;">GSTIN ${gstin}</p>
                        </div>
                    </div>
                    <div style="position:absolute;bottom:24px;left:24px;display:flex;gap:16px;">
                        <div style="display:flex;align-items:center;gap:4px;"><div style="color:#b45309;">${ICO.phone}</div><div style="font-size:7px;color:#78350f;font-weight:700;">${contact}</div></div>
                        <div style="display:flex;align-items:center;gap:4px;"><div style="color:#b45309;">${ICO.mail}</div><div style="font-size:7px;color:#78350f;font-weight:700;">${email}</div></div>
                        <div style="display:flex;align-items:flex-start;gap:4px;"><div style="color:#b45309;margin-top:1px;">${ICO.pin}</div><div style="font-size:7px;color:#78350f;font-weight:700;line-height:1.2;">${address}</div></div>
                    </div>
                </div>
            `
        },
        {
            id: 'sidebar',
            name: 'Studio Vertical',
            desc: 'Striking left sidebar DL format',
            accent: '#7c3aed',
            preview: { headerBg: '#7c3aed', accent: '#7c3aed' },
            render: () => `
                <div class="env-page" style="width:100%;height:100%;box-sizing:border-box;display:flex;background:#ffffff;">
                    <div style="width:40mm;background:linear-gradient(180deg,#5b21b6,#7c3aed);display:flex;flex-direction:column;align-items:center;padding:20px 0;">
                        <img src="${logoUrl}" alt="Logo" style="width:32px;height:auto;max-height:60px;object-fit:contain;"/>
                        <div style="writing-mode:vertical-rl;text-orientation:mixed;transform:rotate(180deg);color:rgba(255,255,255,0.5);font-size:7px;font-weight:900;letter-spacing:4px;text-transform:uppercase;margin-top:auto;margin-bottom:auto;">
                            ${companyName}
                        </div>
                    </div>
                    <div style="flex:1;position:relative;padding:24px;">
                        <h1 style="font-size:22px;font-weight:900;color:#1e1b4b;margin:0;text-transform:uppercase;letter-spacing:1px;line-height:1;">${companyName}</h1>
                        <p style="font-size:7px;font-weight:700;color:#7c3aed;text-transform:uppercase;letter-spacing:3px;margin:4px 0 0 1px;">INTERIOR DESIGNERS & FURNITURE MANUFACTURERS</p>
                        
                        <div style="position:absolute;bottom:24px;left:24px;width:150mm;">
                            <div style="height:2px;background:#7c3aed;width:30px;margin-bottom:12px;"></div>
                            <div style="display:flex;justify-content:space-between;">
                                <div style="display:flex;flex-direction:column;gap:6px;">
                                    <div style="display:flex;align-items:flex-start;gap:6px;"><div style="color:#7c3aed;margin-top:1px;">${ICO.pin}</div><div style="font-size:7px;font-weight:700;color:#334155;line-height:1.2;">${address}</div></div>
                                </div>
                                <div style="display:flex;flex-direction:column;gap:6px;">
                                    <div style="display:flex;align-items:center;gap:6px;"><div style="color:#7c3aed;">${ICO.phone}</div><div style="font-size:7.5px;font-weight:700;color:#334155;">${contact}</div></div>
                                    <div style="display:flex;align-items:center;gap:6px;"><div style="color:#7c3aed;">${ICO.mail}</div><div style="font-size:7.5px;font-weight:700;color:#334155;">${email}</div></div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            `
        },
        {
            id: 'botanical',
            name: 'Botanical Velvet',
            desc: 'Emerald and mint soft tones',
            accent: '#059669',
            preview: { headerBg: '#064e3b', accent: '#10b981' },
            render: () => `
                <div class="env-page" style="width:100%;height:100%;box-sizing:border-box;background:#ecfdf5;position:relative;">
                    <div style="position:absolute;top:0;left:0;right:0;height:40px;background:#064e3b;border-bottom:4px solid #10b981;"></div>
                    <div style="position:relative;z-index:1;padding:16px 32px;display:flex;align-items:center;gap:16px;">
                        <div style="background:#ffffff;padding:12px;border-radius:12px;box-shadow:0 4px 12px rgba(0,0,0,0.1);">
                            <img src="${logoUrl}" alt="Logo" style="height:48px;width:auto;max-width:140px;object-fit:contain;"/>
                        </div>
                        <div>
                            <h1 style="font-size:20px;font-weight:900;color:#111;margin:0;text-transform:uppercase;letter-spacing:1px;line-height:1;">${companyName}</h1>
                            <p style="font-size:6px;font-weight:700;color:#444;text-transform:uppercase;letter-spacing:3px;margin:4px 0 0 2px;">INTERIOR DESIGNERS & FURNITURE MANUFACTURERS</p>
                        </div>
                    </div>
                    <div style="position:absolute;bottom:24px;left:32px;">
                        <div style="display:flex;gap:20px;">
                            <div style="display:flex;align-items:flex-start;gap:6px;"><div style="color:#059669;margin-top:1px;">${ICO.pin}</div><div style="font-size:7.5px;font-weight:700;color:#064e3b;line-height:1.3;">${address}</div></div>
                            <div style="display:flex;align-items:center;gap:6px;"><div style="color:#059669;">${ICO.phone}</div><div style="font-size:8px;font-weight:700;color:#064e3b;">${contact}</div></div>
                        </div>
                    </div>
                    <div style="position:absolute;bottom:24px;right:32px;background:#064e3b;color:white;padding:6px 12px;border-radius:6px;">
                        <p style="font-size:8px;font-weight:800;letter-spacing:2px;margin:0;">GSTIN: ${gstin}</p>
                    </div>
                </div>
            `
        },
        {
            id: 'dark',
            name: 'Contemporary Matte',
            desc: 'Premium dark envelope aesthetic',
            accent: '#38bdf8',
            preview: { headerBg: '#0f172a', accent: '#38bdf8' },
            render: () => `
                <div class="env-page" style="width:100%;height:100%;box-sizing:border-box;background:#0f172a;position:relative;">
                    <div style="padding:32px;display:flex;align-items:center;gap:16px;">
                        <img src="${logoUrl}" alt="Logo" style="height:54px;width:auto;max-width:160px;object-fit:contain;filter:brightness(0) invert(1);"/>
                        <div style="border-left:2px solid #334155;padding-left:16px;">
                            <h1 style="font-size:20px;font-weight:900;color:#ffffff;margin:0;text-transform:uppercase;letter-spacing:2px;line-height:1;">${companyName}</h1>
                            <p style="font-size:6px;font-weight:700;color:#94a3b8;text-transform:uppercase;letter-spacing:3px;margin:4px 0 0 1px;">INTERIOR DESIGNERS & FURNITURE MANUFACTURERS</p>
                        </div>
                    </div>
                    <div style="position:absolute;bottom:0;left:0;right:0;padding:24px 32px;background:#1e293b;border-top:1px solid #334155;display:flex;justify-content:space-between;align-items:center;">
                        <div style="display:flex;gap:24px;">
                            <div style="display:flex;align-items:center;gap:6px;"><div style="color:#38bdf8;">${ICO.phone}</div><div style="font-size:7px;font-weight:700;color:#cbd5e1;">${contact}</div></div>
                            <div style="display:flex;align-items:center;gap:6px;"><div style="color:#38bdf8;">${ICO.mail}</div><div style="font-size:7px;font-weight:700;color:#cbd5e1;">${email}</div></div>
                        </div>
                        <div style="font-size:7px;font-weight:800;color:#94a3b8;letter-spacing:2px;">GSTIN: <span style="color:#ffffff;">${gstin}</span></div>
                    </div>
                </div>
            `
        }
    ];
};

const Envelope = () => {
    const { user, company } = useContext(AuthContext);
    const [copies, setCopies] = useState(1);
    const [selectedDesign, setSelectedDesign] = useState('corporate');

    const companyName = (user?.company_name || 'Your Company Name').toUpperCase();
    const logoUrl = user?.logo
        ? `https://backendibos.laxmaninterior.in/${user.logo}`
        : '/logo.png';
    const designs = getEnvelopeTemplates(companyName, logoUrl, company);
    const activeDesign = designs.find(d => d.id === selectedDesign) || designs[0];

    const handlePrint = () => {
        let pages = '';
        for (let i = 0; i < copies; i++) {
            pages += activeDesign.render();
        }

        const printWindow = window.open('', '_blank');
        printWindow.document.write(`<html><head><title>Print Envelope - ${companyName}</title>
            <style>
                @page { size: 220mm 110mm landscape; margin: 0; }
                body { margin: 0; -webkit-print-color-adjust: exact; print-color-adjust: exact; background: #f8fafc; font-family: 'Inter', sans-serif; }
                .env-page { width: 220mm; height: 110mm; overflow: hidden; box-sizing: border-box; page-break-after: always; page-break-inside: avoid; }
            </style></head><body>${pages}</body></html>`);
        printWindow.document.close();
        printWindow.focus();
        setTimeout(() => printWindow.print(), 500);
    };

    const handleDownloadImage = async () => {
        const element = document.getElementById('env-preview');
        if (!element) return;
        try {
            const dataUrl = await htmlToImage.toPng(element, {
                pixelRatio: 2,
                backgroundColor: '#ffffff',
                skipFonts: true,
                fontEmbedCSS: ''
            });
            const link = document.createElement('a');
            link.download = `\${companyName.replace(/[^a-zA-Z0-9]/g, '')}-Envelope.png`;
            link.href = dataUrl;
            link.click();
        } catch (err) {
            console.error('Error generating image', err);
        }
    };

    const MiniPreview = ({ design }) => {
        const isActive = selectedDesign === design.id;
        return (
            <button
                onClick={() => setSelectedDesign(design.id)}
                className={`flex-shrink-0 w-64 text-left transition-all duration-300 rounded-[24px] overflow-hidden border-2 ${isActive ? 'border-indigo-500 shadow-lg shadow-indigo-200/50 scale-[1.02]' : 'border-slate-100 hover:border-slate-300 hover:shadow-md'}`}
            >
                <div
                    className="h-28 relative flex items-center justify-center p-4"
                    style={{ background: `linear-gradient(135deg, ${design.preview.headerBg}, ${design.preview.accent}20)` }}
                >
                    <div className="w-[180px] h-[90px] bg-white shadow-sm overflow-hidden flex" style={{ borderRadius: '4px' }}>
                        <div className="w-4 h-full" style={{ background: design.preview.accent }}></div>
                        <div className="flex-1 p-2">
                            <div className="h-1.5 w-12 rounded-full mb-1" style={{ background: design.preview.headerBg }}></div>
                            <div className="h-1 w-8 rounded-full mb-4" style={{ background: '#cbd5e1' }}></div>
                            <div className="h-0.5 w-16 rounded-full mt-auto" style={{ background: '#e2e8f0' }}></div>
                        </div>
                    </div>
                </div>
                <div className={`px-5 pb-4 pt-3 ${isActive ? 'bg-indigo-50/50' : 'bg-white'}`}>
                    <h3 className={`text-sm font-black uppercase tracking-wide ${isActive ? 'text-indigo-600' : 'text-slate-900'}`}>
                        {design.name}
                    </h3>
                    <p className="text-[10px] font-semibold text-slate-400 mt-0.5 leading-tight">{design.desc}</p>
                </div>
            </button>
        );
    };

    const previewHtml = activeDesign.render();

    return (
        <div className="space-y-8 p-4 min-h-screen">
            <div className="flex justify-between items-center">
                <div>
                    <h1 className="text-4xl font-black text-slate-900 tracking-tighter italic uppercase">
                        Envelope Studio
                    </h1>
                    <p className="text-slate-400 font-bold text-[10px] uppercase tracking-widest mt-2 italic">
                        {companyName} • Standard DL format (220mm x 110mm)
                    </p>
                </div>
                <div className="flex items-center gap-4">
                    <button onClick={handleDownloadImage} className="bg-white text-indigo-600 border-2 border-indigo-600 px-6 py-3.5 rounded-2xl font-black text-[10px] uppercase shadow-sm hover:bg-indigo-50 transition-all flex items-center gap-2">
                        <Download size={18} /> Download Image
                    </button>
                    <button onClick={handlePrint} className="bg-indigo-600 text-white px-8 py-4 rounded-2xl font-black text-[10px] uppercase shadow-xl hover:bg-slate-900 transition-all flex items-center gap-2">
                        <Printer size={18} /> Print Envelope
                    </button>
                </div>
            </div>

            <div className="bg-white rounded-[40px] border border-slate-100 shadow-sm p-8">
                <div className="flex items-center justify-between mb-6">
                    <h2 className="text-[10px] font-black text-slate-400 uppercase tracking-[0.3em]">Choose DL Template</h2>
                    <span className="text-[10px] font-bold text-indigo-500 uppercase">{designs.length} designs</span>
                </div>
                <div className="flex gap-5 overflow-x-auto pb-4 scrollbar-hide">
                    {designs.map(d => <MiniPreview key={d.id} design={d} />)}
                </div>
            </div>

            <div className="bg-white rounded-[32px] border border-slate-100 shadow-sm p-6 flex items-center gap-6">
                <div className="flex items-center gap-3">
                    <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Copies</span>
                    <div className="flex items-center bg-slate-50 rounded-xl border border-slate-100">
                        <button onClick={() => setCopies(Math.max(1, copies - 1))} className="p-2.5 hover:bg-slate-100 rounded-l-xl text-slate-400"><Minus size={14} /></button>
                        <span className="px-5 py-2 font-black text-indigo-600 text-lg min-w-[50px] text-center">{copies}</span>
                        <button onClick={() => setCopies(Math.min(10, copies + 1))} className="p-2.5 hover:bg-slate-100 rounded-r-xl text-slate-400"><Plus size={14} /></button>
                    </div>
                </div>
            </div>

            <div className="bg-white rounded-[40px] border border-slate-100 shadow-xl overflow-hidden mb-12">
                <div className="bg-slate-900 text-white px-8 py-4 flex justify-between items-center">
                    <div className="flex items-center gap-3">
                        <Mail size={18} className="text-indigo-400" />
                        <span className="text-[10px] font-black uppercase tracking-[0.3em]">Live Preview — {activeDesign.name}</span>
                    </div>
                    <div className="flex items-center gap-2 bg-slate-800 px-3 py-1.5 rounded-lg border border-slate-700">
                        <Check size={12} className="text-emerald-400" />
                        <span className="text-[9px] font-bold text-slate-300 uppercase tracking-widest">Print Ready (DL)</span>
                    </div>
                </div>
                <div className="bg-slate-50 p-12 flex justify-center overflow-auto" style={{ backgroundImage: 'radial-gradient(#cbd5e1 1px, transparent 1px)', backgroundSize: '24px 24px' }}>
                    <div className="shadow-2xl border border-slate-200 overflow-hidden" style={{ maxWidth: '100%' }}>
                        <div
                            id="env-preview"
                            style={{ width: '220mm', height: '110mm', position: 'relative', background: '#ffffff' }}
                            dangerouslySetInnerHTML={{ __html: previewHtml }}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Envelope;
